import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Filter, 
  Download, 
  Search,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  Copy,
  Archive,
  FileText,
  Calendar,
  BarChart3,
  RefreshCw,
  X,
  Save,
  AlertCircle,
  CheckCircle,
  Clock,
  AlertTriangle,
  Megaphone,
  GraduationCap,
  BookOpen,
  PartyPopper,
  Share2,
  TrendingUp,
  Paperclip,
  Upload,
  Star,
  ExternalLink,
  Pin,
  Users,
  Building,
  Hash,
  Globe
} from 'lucide-react';

// Notice Types and Interfaces
export enum NoticeType {
  EXAM = 'exam',
  ADMISSION = 'admission',
  RESULT = 'result',
  ANNOUNCEMENT = 'announcement',
  CIRCULAR = 'circular',
  NOTIFICATION = 'notification',
  HOLIDAY = 'holiday',
  EVENT = 'event',
  SCHOLARSHIP = 'scholarship',
  TENDER = 'tender',
  RECRUITMENT = 'recruitment',
  ACADEMIC = 'academic',
  URGENT = 'urgent'
}

export enum NoticeStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  ARCHIVED = 'archived',
  EXPIRED = 'expired'
}

export enum NoticePriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent'
}

interface Notice {
  _id: string;
  title: string;
  description: string;
  excerpt?: string;
  type: NoticeType;
  status: NoticeStatus;
  priority: NoticePriority;
  attachments: string[];
  featuredImage?: string;
  tags: string[];
  author: {
    _id: string;
    name: string;
    email: string;
    role: string;
    department: string;
    designation: string;
  };
  publishedAt?: Date;
  expiryDate?: Date;
  viewCount: number;
  downloadCount: number;
  isActive: boolean;
  isPinned: boolean;
  showOnHomepage: boolean;
  sendNotification: boolean;
  noticeNumber: string;
  department: string;
  targetAudience: string[];
  metadata?: Record<string, any>;
  createdAt: Date;
  updatedAt: Date;
}

interface NoticeFilter {
  page?: number;
  limit?: number;
  type?: NoticeType;
  status?: NoticeStatus;
  priority?: NoticePriority;
  search?: string;
  department?: string;
  showExpired?: boolean;
  sortBy?: 'publishedAt' | 'priority' | 'viewCount' | 'createdAt' | 'expiryDate';
  sortOrder?: 'asc' | 'desc';
}

interface CreateNoticeRequest {
  title: string;
  description: string;
  excerpt?: string;
  type: NoticeType;
  status?: NoticeStatus;
  priority?: NoticePriority;
  attachments?: string[];
  featuredImage?: string;
  tags?: string[];
  expiryDate?: string;
  isActive?: boolean;
  isPinned?: boolean;
  showOnHomepage?: boolean;
  sendNotification?: boolean;
  noticeNumber?: string;
  department?: string;
  targetAudience?: string[];
  metadata?: Record<string, any>;
}

interface NoticeStats {
  total: number;
  published: number;
  draft: number;
  archived: number;
  expired: number;
  totalViews: number;
  totalDownloads: number;
  thisMonth: number;
  pinned: number;
  byType: Record<NoticeType, number>;
  byPriority: Record<NoticePriority, number>;
}

interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

// API Service for Notices
class NoticeApiService {
  private baseURL: string;

  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    // Ensure endpoint starts with / and construct full URL
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const url = `${this.baseURL}${cleanEndpoint}`;
    
    // Get token from multiple possible sources
    const token = this.getAuthToken();
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    if (token) {
      config.headers = {
        ...config.headers,
        'Authorization': `Bearer ${token}`
      };
    }

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        if (response.status === 401) {
          // Handle unauthorized - redirect to login or refresh token
          this.handleUnauthorized();
          throw new Error('Authentication required. Please log in again.');
        }
        
        const error = await response.json().catch(() => ({ 
          message: `HTTP ${response.status}: ${response.statusText}` 
        }));
        throw new Error(error.message || `HTTP ${response.status}`);
      }

      return response.json();
    } catch (err) {
      console.error('API Request failed:', url, err);
      throw err;
    }
  }

  private getAuthToken(): string | null {
    // Try multiple token storage locations
    return (
      localStorage.getItem('authToken') ||
      localStorage.getItem('token') ||
      localStorage.getItem('accessToken') ||
      sessionStorage.getItem('authToken') ||
      sessionStorage.getItem('token') ||
      null
    );
  }

  private handleUnauthorized(): void {
    // Clear any stored tokens
    localStorage.removeItem('authToken');
    localStorage.removeItem('token');
    localStorage.removeItem('accessToken');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('token');
    
    // Redirect to login page or trigger auth context logout
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  async getNotices(filters: NoticeFilter = {}): Promise<PaginatedResponse<Notice>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, value.toString());
      }
    });
    
    return this.request<PaginatedResponse<Notice>>(`/notices?${params.toString()}`);
  }

  async getNoticeById(id: string): Promise<Notice> {
    return this.request<Notice>(`/notices/${id}`);
  }

  async createNotice(data: CreateNoticeRequest): Promise<Notice> {
    return this.request<Notice>('/notices', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateNotice(id: string, data: Partial<CreateNoticeRequest>): Promise<Notice> {
    return this.request<Notice>(`/notices/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteNotice(id: string): Promise<void> {
    await this.request(`/notices/${id}`, {
      method: 'DELETE',
    });
  }

  async getHomepageNotices(limit: number = 5): Promise<Notice[]> {
    return this.request<Notice[]>(`/notices/homepage?limit=${limit}`);
  }

  async getPinnedNotices(): Promise<Notice[]> {
    return this.request<Notice[]>('/notices/pinned');
  }

  async getLatestNotices(limit: number = 10): Promise<Notice[]> {
    return this.request<Notice[]>(`/notices/latest?limit=${limit}`);
  }

  async getNoticesByType(type: NoticeType, limit: number = 10): Promise<Notice[]> {
    return this.request<Notice[]>(`/notices/published/${type}?limit=${limit}`);
  }

  async searchNotices(query: string, filters?: any): Promise<Notice[]> {
    const params = new URLSearchParams({ q: query, ...filters });
    return this.request<Notice[]>(`/notices/search?${params}`);
  }

  async uploadFiles(files: FileList): Promise<{ success: boolean; files: Array<{ filename: string; originalName: string; url: string }> }> {
    const formData = new FormData();
    Array.from(files).forEach(file => {
      formData.append('files', file);
    });

    const token = this.getAuthToken();
    const headers: Record<string, string> = {};
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseURL}/notices/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      if (response.status === 401) {
        this.handleUnauthorized();
        throw new Error('Authentication required. Please log in again.');
      }
      
      const error = await response.json().catch(() => ({ 
        message: `Upload failed: ${response.statusText}` 
      }));
      throw new Error(error.message || 'Upload failed');
    }

    return response.json();
  }

  async downloadAttachment(noticeId: string, filename: string): Promise<void> {
    const url = `${this.baseURL}/notices/download/${noticeId}/${filename}`;
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  async getNoticeStats(): Promise<NoticeStats> {
    try {
      return this.request<NoticeStats>('/notices/stats');
    } catch (error) {
      // Fallback calculation
      const allNotices = await this.getNotices({ limit: 1000 });
      const now = new Date();
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      
      const stats: NoticeStats = {
        total: allNotices.total,
        published: allNotices.data.filter(n => n.status === NoticeStatus.PUBLISHED).length,
        draft: allNotices.data.filter(n => n.status === NoticeStatus.DRAFT).length,
        archived: allNotices.data.filter(n => n.status === NoticeStatus.ARCHIVED).length,
        expired: allNotices.data.filter(n => n.expiryDate && new Date(n.expiryDate) < now).length,
        totalViews: allNotices.data.reduce((sum, n) => sum + n.viewCount, 0),
        totalDownloads: allNotices.data.reduce((sum, n) => sum + n.downloadCount, 0),
        thisMonth: allNotices.data.filter(n => new Date(n.createdAt) >= thisMonthStart).length,
        pinned: allNotices.data.filter(n => n.isPinned).length,
        byType: Object.values(NoticeType).reduce((acc, type) => {
          acc[type] = allNotices.data.filter(n => n.type === type).length;
          return acc;
        }, {} as Record<NoticeType, number>),
        byPriority: Object.values(NoticePriority).reduce((acc, priority) => {
          acc[priority] = allNotices.data.filter(n => n.priority === priority).length;
          return acc;
        }, {} as Record<NoticePriority, number>),
      };
      
      return stats;
    }
  }

  async bulkUpdateStatus(ids: string[], status: NoticeStatus): Promise<{ success: boolean; processed: number; failed: number }> {
    return this.request(`/notices/bulk/status`, {
      method: 'PATCH',
      body: JSON.stringify({ ids, status }),
    });
  }

  async bulkDelete(ids: string[]): Promise<{ success: boolean; processed: number; failed: number }> {
    return this.request(`/notices/bulk`, {
      method: 'DELETE',
      body: JSON.stringify({ ids }),
    });
  }
}

const noticeService = new NoticeApiService();

// Mock notification hook
const useNotification = () => ({
  success: (title: string, message?: string) => {
    console.log(`Success: ${title}${message ? ` - ${message}` : ''}`);
  },
  error: (title: string, message?: string) => {
    console.error(`Error: ${title}${message ? ` - ${message}` : ''}`);
  },
  warning: (title: string, message?: string) => {
    console.warn(`Warning: ${title}${message ? ` - ${message}` : ''}`);
  },
  info: (title: string, message?: string) => {
    console.info(`Info: ${title}${message ? ` - ${message}` : ''}`);
  },
});

// Loading Spinner Component
const LoadingSpinner: React.FC<{ size?: 'sm' | 'md' | 'lg'; text?: string }> = ({ 
  size = 'md', 
  text 
}) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };

  return (
    <div className="flex flex-col items-center justify-center p-4">
      <RefreshCw className={`${sizeClasses[size]} animate-spin text-blue-600`} />
      {text && <p className="mt-2 text-sm text-gray-600">{text}</p>}
    </div>
  );
};

// File Upload Component
interface FileUploadProps {
  onFilesUploaded: (filenames: string[]) => void;
  maxFiles?: number;
  acceptedTypes?: string[];
}

const FileUpload: React.FC<FileUploadProps> = ({ onFilesUploaded, maxFiles = 10, acceptedTypes }) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const { success, error } = useNotification();

  const handleFiles = async (files: FileList) => {
    if (files.length === 0) return;

    if (files.length > maxFiles) {
      error('Too many files', `Maximum ${maxFiles} files allowed`);
      return;
    }

    setUploading(true);
    try {
      const result = await noticeService.uploadFiles(files);
      if (result.success) {
        const filenames = result.files.map(f => f.filename);
        onFilesUploaded(filenames);
        success('Files uploaded', `${result.files.length} files uploaded successfully`);
      }
    } catch (err: any) {
      error('Upload failed', err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  return (
    <div
      className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
        dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
      }`}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
      <div className="text-sm text-gray-600 mb-2">
        Drop files here or{' '}
        <label className="text-blue-600 hover:text-blue-800 cursor-pointer">
          browse
          <input
            type="file"
            multiple
            className="hidden"
            accept={acceptedTypes?.join(',')}
            onChange={(e) => e.target.files && handleFiles(e.target.files)}
            disabled={uploading}
          />
        </label>
      </div>
      <div className="text-xs text-gray-500">
        {acceptedTypes ? `Supports: ${acceptedTypes.join(', ')}` : 'Supports: PDF, DOC, XLS, Images'}
      </div>
      {uploading && (
        <div className="mt-4">
          <LoadingSpinner size="sm" text="Uploading..." />
        </div>
      )}
    </div>
  );
};

// Notice Modal Component
interface NoticeModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  notice?: Notice | null;
  mode: 'create' | 'edit';
}

const NoticeModal: React.FC<NoticeModalProps> = ({
  open,
  onClose,
  onSuccess,
  notice,
  mode,
}) => {
  const [formData, setFormData] = useState<CreateNoticeRequest>({
    title: '',
    description: '',
    type: NoticeType.ANNOUNCEMENT,
    status: NoticeStatus.DRAFT,
    priority: NoticePriority.MEDIUM,
    isActive: true,
    tags: [],
    attachments: [],
    targetAudience: [],
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { success, error } = useNotification();

  useEffect(() => {
    if (open) {
      if (notice && mode === 'edit') {
        setFormData({
          title: notice.title,
          description: notice.description,
          excerpt: notice.excerpt,
          type: notice.type,
          status: notice.status,
          priority: notice.priority,
          attachments: notice.attachments,
          featuredImage: notice.featuredImage,
          tags: notice.tags,
          expiryDate: notice.expiryDate ? new Date(notice.expiryDate).toISOString().split('T')[0] : undefined,
          isActive: notice.isActive,
          isPinned: notice.isPinned,
          showOnHomepage: notice.showOnHomepage,
          sendNotification: notice.sendNotification,
          noticeNumber: notice.noticeNumber,
          department: notice.department,
          targetAudience: notice.targetAudience,
          metadata: notice.metadata,
        });
      } else {
        setFormData({
          title: '',
          description: '',
          type: NoticeType.ANNOUNCEMENT,
          status: NoticeStatus.DRAFT,
          priority: NoticePriority.MEDIUM,
          isActive: true,
          tags: [],
          attachments: [],
          targetAudience: [],
        });
      }
      setErrors({});
    }
  }, [open, notice, mode]);

  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      if (mode === 'edit' && notice) {
        await noticeService.updateNotice(notice._id, formData);
        success('Notice updated', 'Notice has been updated successfully');
      } else {
        await noticeService.createNotice(formData);
        success('Notice created', 'Notice has been created successfully');
      }
      onSuccess();
    } catch (err: any) {
      error('Operation failed', err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [validateForm, mode, notice, formData, success, error, onSuccess]);

  const handleInputChange = useCallback((field: string, value: any) => {
    setFormData((prev: CreateNoticeRequest) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev: Record<string, string>) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [errors]);

  const handleFilesUploaded = useCallback((filenames: string[]) => {
    setFormData(prev => ({
      ...prev,
      attachments: [...(prev.attachments || []), ...filenames]
    }));
  }, []);

  const removeAttachment = useCallback((filename: string) => {
    setFormData(prev => ({
      ...prev,
      attachments: (prev.attachments || []).filter(f => f !== filename)
    }));
  }, []);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {mode === 'edit' ? 'Edit Notice' : 'Create New Notice'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Title */}
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter notice title..."
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                )}
              </div>

              {/* Type and Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type *
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={NoticeType.ANNOUNCEMENT}>📢 Announcement</option>
                  <option value={NoticeType.EXAM}>📝 Exam</option>
                  <option value={NoticeType.ADMISSION}>🎓 Admission</option>
                  <option value={NoticeType.RESULT}>📊 Result</option>
                  <option value={NoticeType.CIRCULAR}>📋 Circular</option>
                  <option value={NoticeType.SCHOLARSHIP}>💰 Scholarship</option>
                  <option value={NoticeType.EVENT}>🎉 Event</option>
                  <option value={NoticeType.HOLIDAY}>🏖️ Holiday</option>
                  <option value={NoticeType.RECRUITMENT}>👥 Recruitment</option>
                  <option value={NoticeType.TENDER}>📄 Tender</option>
                  <option value={NoticeType.URGENT}>🚨 Urgent</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={NoticePriority.LOW}>🟢 Low</option>
                  <option value={NoticePriority.MEDIUM}>🟡 Medium</option>
                  <option value={NoticePriority.HIGH}>🟠 High</option>
                  <option value={NoticePriority.URGENT}>🔴 Urgent</option>
                </select>
              </div>

              {/* Status and Expiry */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={NoticeStatus.DRAFT}>📝 Draft</option>
                  <option value={NoticeStatus.PUBLISHED}>✅ Published</option>
                  <option value={NoticeStatus.ARCHIVED}>📦 Archived</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expiry Date
                </label>
                <input
                  type="date"
                  value={formData.expiryDate || ''}
                  onChange={(e) => handleInputChange('expiryDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Checkboxes */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  className="mr-2 text-green-600 focus:ring-green-500"
                />
                <span className="text-sm font-medium text-gray-700">✅ Active</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isPinned}
                  onChange={(e) => handleInputChange('isPinned', e.target.checked)}
                  className="mr-2 text-yellow-600 focus:ring-yellow-500"
                />
                <span className="text-sm font-medium text-gray-700">📌 Pinned</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.showOnHomepage}
                  onChange={(e) => handleInputChange('showOnHomepage', e.target.checked)}
                  className="mr-2 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">🏠 Homepage</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.sendNotification}
                  onChange={(e) => handleInputChange('sendNotification', e.target.checked)}
                  className="mr-2 text-purple-600 focus:ring-purple-500"
                />
                <span className="text-sm font-medium text-gray-700">🔔 Notify</span>
              </label>
            </div>

            {/* Excerpt */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Excerpt
              </label>
              <textarea
                value={formData.excerpt || ''}
                onChange={(e) => handleInputChange('excerpt', e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Brief summary of the notice..."
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={6}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.description ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Write your notice description here..."
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">{errors.description}</p>
              )}
            </div>

            {/* Additional Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notice Number
                </label>
                <input
                  type="text"
                  value={formData.noticeNumber || ''}
                  onChange={(e) => handleInputChange('noticeNumber', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="TSBIE/TYPE/YEAR/001"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Department
                </label>
                <input
                  type="text"
                  value={formData.department || ''}
                  onChange={(e) => handleInputChange('department', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Board of Intermediate Education"
                />
              </div>
            </div>

                   {/* Tags and Target Audience */}
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags
                </label>
                <input
                  type="text"
                  value={formData.tags?.join(', ') || ''}
                  onChange={(e) => handleInputChange('tags', e.target.value.split(',').map(tag => tag.trim()).filter(Boolean))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter tags separated by commas..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Audience
                </label>
                <input
                  type="text"
                  value={formData.targetAudience?.join(', ') || ''}
                  onChange={(e) => handleInputChange('targetAudience', e.target.value.split(',').map(audience => audience.trim()).filter(Boolean))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="students, colleges, parents..."
                />
              </div>
            </div>

            {/* Featured Image */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Featured Image URL
              </label>
              <input
                type="url"
                value={formData.featuredImage || ''}
                onChange={(e) => handleInputChange('featuredImage', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://example.com/image.jpg"
              />
            </div>

            {/* File Attachments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attachments
              </label>
              
              {/* Current Attachments */}
              {formData.attachments && formData.attachments.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Current Attachments:</h4>
                  <div className="space-y-2">
                    {formData.attachments.map((filename, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center">
                          <Paperclip className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-700">{filename}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAttachment(filename)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* File Upload */}
              <FileUpload
                onFilesUploaded={handleFilesUploaded}
                maxFiles={10}
                acceptedTypes={['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.jpeg', '.png', '.gif']}
              />
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
          >
            {loading && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
            <Save className="w-4 h-4 mr-2" />
            {mode === 'edit' ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Notice Management Component
export const NoticeManagement: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  // Rename to avoid conflict with error state
  const { success, error: notificationError } = useNotification();

  // State management
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedNotices, setSelectedNotices] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<NoticeStats>({
    total: 0,
    published: 0,
    draft: 0,
    archived: 0,
    expired: 0,
    totalViews: 0,
    totalDownloads: 0,
    thisMonth: 0,
    pinned: 0,
    byType: {} as Record<NoticeType, number>,
    byPriority: {} as Record<NoticePriority, number>,
  });

  // Filter state
  const filters = useMemo(() => ({
    page: parseInt(searchParams.get('page') || '1'),
    limit: parseInt(searchParams.get('limit') || '10'),
    type: searchParams.get('type') as NoticeType || undefined,
    status: searchParams.get('status') as NoticeStatus || undefined,
    priority: searchParams.get('priority') as NoticePriority || undefined,
    search: searchParams.get('search') || '',
    department: searchParams.get('department') || undefined,
    showExpired: searchParams.get('showExpired') === 'true',
    sortBy: searchParams.get('sortBy') as any || 'publishedAt',
    sortOrder: searchParams.get('sortOrder') as any || 'desc',
  }), [searchParams]);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingNotice, setEditingNotice] = useState<Notice | null>(null);

  // Fetch notices data
  const fetchNotices = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) {
        setLoading(true);
        setError(null);
      } else {
        setRefreshing(true);
      }

      const response = await noticeService.getNotices(filters);
      setNotices(response.data || []);
      setPagination({
        current: response.page || 1,
        pageSize: filters.limit || 10,
        total: response.total || 0,
      });
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch notices:', err);
      
      const errorMessage = err.message || 'Unable to connect to server';
      setError(errorMessage);
      
      if (err.message.includes('Authentication required')) {
        notificationError('Authentication Error', 'Please log in to access notices');
      } else {
        notificationError('Failed to load notices', errorMessage);
      }
      
      // Set empty state on error
      setNotices([]);
      setPagination({ current: 1, pageSize: 10, total: 0 });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [
    filters.page, 
    filters.limit, 
    filters.type, 
    filters.status, 
    filters.priority, 
    filters.search, 
    filters.department, 
    filters.showExpired, 
    filters.sortBy, 
    filters.sortOrder
  ]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const statsData = await noticeService.getNoticeStats();
      setStats(statsData);
    } catch (err: any) {
      console.error('Failed to fetch stats:', err);
      
      // Set empty stats on error
      setStats({
        total: 0,
        published: 0,
        draft: 0,
        archived: 0,
        expired: 0,
        totalViews: 0,
        totalDownloads: 0,
        thisMonth: 0,
        pinned: 0,
        byType: {} as Record<NoticeType, number>,
        byPriority: {} as Record<NoticePriority, number>,
      });
    }
  }, []);

  // Effects with timeout protection
  useEffect(() => {
    let timeoutId: number;
    
    const loadData = async () => {
      // Set a timeout to ensure loading state doesn't get stuck
      timeoutId = setTimeout(() => {
        setLoading(false);
        setRefreshing(false);
        setError('Request timed out. Please try again.');
      }, 30000); // 30 second timeout

      try {
        await fetchNotices();
      } finally {
        clearTimeout(timeoutId);
      }
    };

    loadData();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [filters.page, filters.limit, filters.type, filters.status, filters.priority, filters.search, filters.department, filters.showExpired, filters.sortBy, filters.sortOrder]);

  useEffect(() => {
    // Fetch stats only once on mount
    fetchStats();
  }, [fetchStats]);

  // Handlers
  const handleFilterChange = useCallback((key: string, value: any) => {
    const newParams = new URLSearchParams(searchParams);
    
    if (value && value !== '') {
      newParams.set(key, value.toString());
    } else {
      newParams.delete(key);
    }
    
    if (key !== 'page') {
      newParams.set('page', '1');
    }
    
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const handleCreate = useCallback(() => {
    setModalMode('create');
    setEditingNotice(null);
    setShowModal(true);
  }, []);

  const handleEdit = useCallback((notice: Notice) => {
    setModalMode('edit');
    setEditingNotice(notice);
    setShowModal(true);
  }, []);

  const handleDelete = useCallback(async (id: string) => {
    if (window.confirm('Are you sure you want to delete this notice?')) {
      try {
        await noticeService.deleteNotice(id);
        success('Notice deleted', 'Notice has been deleted successfully');
        fetchNotices(false);
        fetchStats();
      } catch (err: any) {
        notificationError('Delete failed', err.message || 'Unable to delete notice. Please try again.');
      }
    }
  }, [success, notificationError, fetchNotices, fetchStats]);

  const handleBulkAction = useCallback(async (action: 'publish' | 'archive' | 'delete') => {
    if (selectedNotices.length === 0) {
      notificationError('No notices selected', 'Please select notices to perform bulk action');
      return;
    }

    const confirmMessage = `Are you sure you want to ${action} ${selectedNotices.length} notices?`;
    if (!window.confirm(confirmMessage)) return;

    try {
      if (action === 'delete') {
        await noticeService.bulkDelete(selectedNotices);
      } else {
        const status = action === 'publish' ? NoticeStatus.PUBLISHED : NoticeStatus.ARCHIVED;
        await noticeService.bulkUpdateStatus(selectedNotices, status);
      }
      
      success(`Bulk ${action} completed`, `${selectedNotices.length} notices ${action}ed successfully`);
      setSelectedNotices([]);
      fetchNotices(false);
      fetchStats();
    } catch (err: any) {
      notificationError(`Bulk ${action} failed`, err.message);
    }
  }, [selectedNotices, success, notificationError, fetchNotices, fetchStats]);

  const handleDownload = useCallback(async (noticeId: string, filename: string) => {
    try {
      await noticeService.downloadAttachment(noticeId, filename);
    } catch (err: any) {
      notificationError('Download failed', err.message);
    }
  }, [notificationError]);

  const handleModalSuccess = useCallback(() => {
    setShowModal(false);
    setEditingNotice(null);
    setTimeout(() => {
      fetchNotices(false);
      fetchStats();
    }, 100);
  }, [fetchNotices, fetchStats]);

  const toggleSelectNotice = useCallback((id: string) => {
    setSelectedNotices(prev => 
      prev.includes(id) 
        ? prev.filter(nId => nId !== id)
        : [...prev, id]
    );
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedNotices(prev => 
      prev.length === notices.length ? [] : notices.map(n => n._id)
    );
  }, [notices]);

  // Utility functions
  const getTypeIcon = useCallback((type: NoticeType) => {
    const icons = {
      [NoticeType.ANNOUNCEMENT]: '📢',
      [NoticeType.EXAM]: '📝',
      [NoticeType.ADMISSION]: '🎓',
      [NoticeType.RESULT]: '📊',
      [NoticeType.CIRCULAR]: '📋',
      [NoticeType.SCHOLARSHIP]: '💰',
      [NoticeType.EVENT]: '🎉',
      [NoticeType.HOLIDAY]: '🏖️',
      [NoticeType.RECRUITMENT]: '👥',
      [NoticeType.TENDER]: '📄',
      [NoticeType.URGENT]: '🚨',
      [NoticeType.ACADEMIC]: '📚',
      [NoticeType.NOTIFICATION]: '🔔',
    };
    return icons[type] || '📄';
  }, []);

  const getTypeColor = useCallback((type: NoticeType) => {
    const colors = {
      [NoticeType.ANNOUNCEMENT]: 'bg-blue-100 text-blue-800',
      [NoticeType.EXAM]: 'bg-orange-100 text-orange-800',
      [NoticeType.ADMISSION]: 'bg-green-100 text-green-800',
      [NoticeType.RESULT]: 'bg-purple-100 text-purple-800',
      [NoticeType.CIRCULAR]: 'bg-gray-100 text-gray-800',
      [NoticeType.SCHOLARSHIP]: 'bg-yellow-100 text-yellow-800',
      [NoticeType.EVENT]: 'bg-pink-100 text-pink-800',
      [NoticeType.HOLIDAY]: 'bg-teal-100 text-teal-800',
      [NoticeType.RECRUITMENT]: 'bg-indigo-100 text-indigo-800',
      [NoticeType.TENDER]: 'bg-cyan-100 text-cyan-800',
      [NoticeType.URGENT]: 'bg-red-100 text-red-800',
      [NoticeType.ACADEMIC]: 'bg-emerald-100 text-emerald-800',
      [NoticeType.NOTIFICATION]: 'bg-violet-100 text-violet-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  }, []);

  const getPriorityColor = useCallback((priority: NoticePriority) => {
    const colors = {
      [NoticePriority.LOW]: 'bg-green-100 text-green-800',
      [NoticePriority.MEDIUM]: 'bg-yellow-100 text-yellow-800',
      [NoticePriority.HIGH]: 'bg-orange-100 text-orange-800',
      [NoticePriority.URGENT]: 'bg-red-100 text-red-800',
    };
    return colors[priority];
  }, []);

  const getStatusColor = useCallback((status: NoticeStatus) => {
    const colors = {
      [NoticeStatus.DRAFT]: 'bg-gray-100 text-gray-800',
      [NoticeStatus.PUBLISHED]: 'bg-green-100 text-green-800',
      [NoticeStatus.ARCHIVED]: 'bg-blue-100 text-blue-800',
      [NoticeStatus.EXPIRED]: 'bg-red-100 text-red-800',
    };
    return colors[status];
  }, []);

  const formatDate = useCallback((date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <LoadingSpinner size="lg" text="Loading notices..." />
        {error && (
          <div className="text-center">
            <p className="text-red-600 mb-2">{error}</p>
            <button
              onClick={() => {
                setLoading(false);
                setError(null);
                setNotices([]);
              }}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Stop Loading
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Notice Management</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage official notices, circulars, and announcements for Telangana Intermediate Board
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button
            onClick={() => fetchNotices(false)}
            disabled={refreshing}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleCreate}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Notice
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total</p>
              <p className="text-lg font-semibold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Published</p>
              <p className="text-lg font-semibold text-gray-900">{stats.published}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Edit className="w-5 h-5 text-yellow-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Draft</p>
              <p className="text-lg font-semibold text-gray-900">{stats.draft}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Archive className="w-5 h-5 text-gray-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Archived</p>
              <p className="text-lg font-semibold text-gray-900">{stats.archived}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <Clock className="w-5 h-5 text-red-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Expired</p>
              <p className="text-lg font-semibold text-gray-900">{stats.expired}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Eye className="w-5 h-5 text-purple-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Views</p>
              <p className="text-lg font-semibold text-gray-900">{stats.totalViews.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Download className="w-5 h-5 text-indigo-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Downloads</p>
              <p className="text-lg font-semibold text-gray-900">{stats.totalDownloads.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Pin className="w-5 h-5 text-orange-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Pinned</p>
              <p className="text-lg font-semibold text-gray-900">{stats.pinned}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white p-4 rounded-lg shadow-sm border space-y-4">
        {/* Search and Filters */}
        <div className="flex flex-col lg:flex-row lg:items-center space-y-4 lg:space-y-0 lg:space-x-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search notices..."
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Type Filter */}
          <select
            value={filters.type || ''}
            onChange={(e) => handleFilterChange('type', e.target.value || undefined)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Types</option>
            {Object.values(NoticeType).map(type => (
              <option key={type} value={type}>
                {getTypeIcon(type)} {type.charAt(0).toUpperCase() + type.slice(1)}
              </option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={filters.status || ''}
            onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Status</option>
            {Object.values(NoticeStatus).map(status => (
              <option key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </option>
            ))}
          </select>

          {/* Priority Filter */}
          <select
            value={filters.priority || ''}
            onChange={(e) => handleFilterChange('priority', e.target.value || undefined)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Priorities</option>
            {Object.values(NoticePriority).map(priority => (
              <option key={priority} value={priority}>
                {priority.charAt(0).toUpperCase() + priority.slice(1)}
              </option>
            ))}
          </select>

          {/* Sort */}
          <select
            value={`${filters.sortBy}-${filters.sortOrder}`}
            onChange={(e) => {
              const [sortBy, sortOrder] = e.target.value.split('-');
              handleFilterChange('sortBy', sortBy);
              handleFilterChange('sortOrder', sortOrder);
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="publishedAt-desc">Latest First</option>
            <option value="publishedAt-asc">Oldest First</option>
            <option value="priority-desc">High Priority</option>
            <option value="viewCount-desc">Most Viewed</option>
            <option value="createdAt-desc">Recently Created</option>
          </select>

          {/* Show Expired */}
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={filters.showExpired}
              onChange={(e) => handleFilterChange('showExpired', e.target.checked)}
              className="mr-2 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Show Expired</span>
          </label>

          {/* Page Size */}
          <select
            value={filters.limit || 10}
            onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
          </select>
        </div>

        {/* Bulk Actions */}
        {selectedNotices.length > 0 && (
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
            <span className="text-sm text-blue-900">
              {selectedNotices.length} notice(s) selected
            </span>
            <div className="flex space-x-2">
              <button
                onClick={() => handleBulkAction('publish')}
                className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
              >
                Publish
              </button>
              <button
                onClick={() => handleBulkAction('archive')}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
              >
                Archive
              </button>
              <button
                onClick={() => handleBulkAction('delete')}
                className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
              >
                Delete
              </button>
              <button
                onClick={() => setSelectedNotices([])}
                className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Notices Table */}
      <div className="bg-white shadow-sm border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedNotices.length === notices.length && notices.length > 0}
                    onChange={toggleSelectAll}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Notice Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type & Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statistics
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dates
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {notices.map((notice) => (
                <tr key={notice._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedNotices.includes(notice._id)}
                      onChange={() => toggleSelectNotice(notice._id)}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="max-w-xs">
                      <div className="flex items-center">
                        {notice.isPinned && (
                          <Pin className="w-4 h-4 text-yellow-500 mr-2 flex-shrink-0" />
                        )}
                        <div className="font-medium text-gray-900 truncate">{notice.title}</div>
                      </div>
                      <div className="text-sm text-gray-500 truncate mt-1">{notice.excerpt || notice.description}</div>
                      <div className="flex items-center gap-2 mt-1">
                        {notice.noticeNumber && (
                          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                            {notice.noticeNumber}
                          </span>
                        )}
                        {notice.department && (
                          <span className="text-xs text-gray-400">
                            {notice.department}
                          </span>
                        )}
                      </div>
                      {notice.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {notice.tags.slice(0, 3).map((tag, index) => (
                            <span
                              key={index}
                              className="text-xs text-gray-500 bg-gray-100 px-1 py-0.5 rounded"
                            >
                              #{tag}
                            </span>
                          ))}
                          {notice.tags.length > 3 && (
                            <span className="text-xs text-gray-400">+{notice.tags.length - 3}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(notice.type)}`}>
                        <span className="mr-1">{getTypeIcon(notice.type)}</span>
                        {notice.type.charAt(0).toUpperCase() + notice.type.slice(1)}
                      </span>
                      <div>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(notice.priority)}`}>
                          {notice.priority.charAt(0).toUpperCase() + notice.priority.slice(1)}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col space-y-1">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(notice.status)}`}>
                        {notice.status === NoticeStatus.PUBLISHED && <CheckCircle className="w-3 h-3 mr-1" />}
                        {notice.status === NoticeStatus.DRAFT && <Edit className="w-3 h-3 mr-1" />}
                        {notice.status === NoticeStatus.ARCHIVED && <Archive className="w-3 h-3 mr-1" />}
                        {notice.status.charAt(0).toUpperCase() + notice.status.slice(1)}
                      </span>
                      {notice.expiryDate && new Date(notice.expiryDate) < new Date() && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <Clock className="w-3 h-3 mr-1" />
                          Expired
                        </span>
                      )}
                      {notice.showOnHomepage && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Homepage
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1 text-sm text-gray-900">
                      <div className="flex items-center">
                        <Eye className="w-4 h-4 text-gray-400 mr-2" />
                        {notice.viewCount.toLocaleString()}
                      </div>
                      {notice.downloadCount > 0 && (
                        <div className="flex items-center">
                          <Download className="w-4 h-4 text-gray-400 mr-2" />
                          {notice.downloadCount.toLocaleString()}
                        </div>
                      )}
                      {notice.attachments.length > 0 && (
                        <div className="flex items-center">
                          <Paperclip className="w-4 h-4 text-gray-400 mr-2" />
                          {notice.attachments.length} files
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <div className="space-y-1">
                      {notice.publishedAt && (
                        <div>
                          <span className="text-gray-500">Published:</span><br />
                          {formatDate(notice.publishedAt)}
                        </div>
                      )}
                      {notice.expiryDate && (
                        <div>
                          <span className="text-gray-500">Expires:</span><br />
                          <span className={new Date(notice.expiryDate) < new Date() ? 'text-red-600' : 'text-gray-900'}>
                            {formatDate(notice.expiryDate)}
                          </span>
                        </div>
                      )}
                      <div className="text-xs text-gray-400">
                        Created: {formatDate(notice.createdAt)}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      {notice.attachments.length > 0 && (
                        <div className="relative group">
                          <button className="text-green-600 hover:text-green-900" title="Download Attachments">
                            <Download className="w-4 h-4" />
                          </button>
                          <div className="absolute right-0 top-8 w-48 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                            <div className="p-2 space-y-1">
                              {notice.attachments.map((filename, index) => (
                                <button
                                  key={index}
                                  onClick={() => handleDownload(notice._id, filename)}
                                  className="w-full text-left px-2 py-1 text-sm text-gray-700 hover:bg-gray-100 rounded flex items-center"
                                >
                                  <Paperclip className="w-3 h-3 mr-2" />
                                  <span className="truncate">{filename}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                      <button
                        onClick={() => handleEdit(notice)}
                        className="text-gray-600 hover:text-gray-900"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => navigate(`/notices/${notice._id}`)}
                        className="text-blue-600 hover:text-blue-900"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(notice._id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {notices.length === 0 && !loading && (
          <div className="text-center py-12">
            <Megaphone className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No notices found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {filters.search || filters.type || filters.status || filters.priority
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by creating your first notice.'
              }
            </p>
            <div className="mt-6">
              <button
                onClick={handleCreate}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Notice
              </button>
            </div>
          </div>
        )}

        {/* Pagination */}
        {notices.length > 0 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handleFilterChange('page', Math.max(1, pagination.current - 1))}
                disabled={pagination.current <= 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => handleFilterChange('page', pagination.current + 1)}
                disabled={pagination.current * pagination.pageSize >= pagination.total}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">
                    {(pagination.current - 1) * pagination.pageSize + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(pagination.current * pagination.pageSize, pagination.total)}
                  </span>{' '}
                  of <span className="font-medium">{pagination.total}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handleFilterChange('page', Math.max(1, pagination.current - 1))}
                    disabled={pagination.current <= 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: Math.ceil(pagination.total / pagination.pageSize) }, (_, i) => i + 1)
                    .filter(page => 
                      page === 1 || 
                      page === Math.ceil(pagination.total / pagination.pageSize) || 
                      Math.abs(page - pagination.current) <= 2
                    )
                    .map((page, index, arr) => {
                      const showEllipsis = index > 0 && page - arr[index - 1] > 1;
                      return (
                        <React.Fragment key={page}>
                          {showEllipsis && (
                            <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                              ...
                            </span>
                          )}
                          <button
                            onClick={() => handleFilterChange('page', page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              page === pagination.current
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      );
                    })}
                  
                  <button
                    onClick={() => handleFilterChange('page', pagination.current + 1)}
                    disabled={pagination.current * pagination.pageSize >= pagination.total}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Notice Modal */}
      <NoticeModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onSuccess={handleModalSuccess}
        notice={editingNotice}
        mode={modalMode}
      />
    </div>
  );
};

export default NoticeManagement;